import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Badge,
  Spinner,
} from "reactstrap";
import Swal from "sweetalert2";
import axios from "axios";
import moment from "moment";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

const sec = {
  borderLeft: "4px solid #3b82f6",
  paddingLeft: 12,
  marginBottom: 8,
  color: "#1e3a8a",
  fontWeight: 600,
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const block = {
  background: "#f8fafc",
  borderRadius: 8,
  padding: "12px 16px",
  marginBottom: 14,
};

const TABLE_HEADERS = [
  { name: "#", field: "idPromocionWalmart", sortable: true },
  { name: "Artículo", field: "nombre", sortable: true },
  { name: "Marca", field: "marca", sortable: true },
  { name: "Categoría", field: "categoria", sortable: true },
  { name: "Descuento", field: "descuento", sortable: true },
  { name: "Tipo", field: "tipoDescuento", sortable: true },
  { name: "F.Inicio", field: "fechaInicio", sortable: true },
  { name: "F.Fin", field: "fechaFin", sortable: true },
  { name: "Acciones", field: "acciones", sortable: false },
];

function ListadoPromocionesWalmart() {
  const { user } = useContext(AuthContext);
  const URL_PROMOCIONES_WALMART = process.env.REACT_APP_URL_PROMOCIONES_WALMART;

  const hoy = moment().format("YYYY-MM-DD");

  const [comments, setComments] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "idPromocionWalmart", order: "desc" });
  const ITEMS_PER_PAGE = 50;

  // Filters
  const [filterFechaInicio, setFilterFechaInicio] = useState("");
  const [filterFechaFin, setFilterFechaFin] = useState("");
  const [filterMarca, setFilterMarca] = useState("");

  // Edit modal
  const [modalEdit, setModalEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [editDescuento, setEditDescuento] = useState(0);
  const [editTipoDescuento, setEditTipoDescuento] = useState("Porcentaje");
  const [editFechaInicio, setEditFechaInicio] = useState(hoy);
  const [editFechaFin, setEditFechaFin] = useState(hoy);
  const [editMarca, setEditMarca] = useState("");
  const [editCategoria, setEditCategoria] = useState("");
  const [validaBotonEdit, setValidaBotonEdit] = useState(true);

  useEffect(() => {
    if (user.menu_ventas) {
      fetchData();
    }
  }, []);

  const fetchData = () => {
    showLoader();
    axios
      .get(URL_PROMOCIONES_WALMART, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => {
        const mapped = res.data.map((p) => ({
          _id: p._id,
          idPromocionWalmart: p.idPromocionWalmart,
          nombre: p.articulos && p.articulos[0] ? p.articulos[0].nombre : "N/A",
          descuento: p.descuento,
          tipoDescuento: p.tipoDescuento,
          fechaInicio: p.fechaInicio,
          fechaFin: p.fechaFin,
          marca: p.marca,
          categoria: p.categoria,
          is_active: p.is_active,
        }));
        setComments(mapped);
        hideLoader();
      })
      .catch(() => {
        hideLoader();
        Swal.fire({ icon: "error", title: "Error al cargar promociones" });
      });
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setEditDescuento(row.descuento);
    setEditTipoDescuento(row.tipoDescuento);
    setEditFechaInicio(row.fechaInicio);
    setEditFechaFin(row.fechaFin);
    setEditMarca(row.marca);
    setEditCategoria(row.categoria);
    setValidaBotonEdit(true);
    setModalEdit(true);
  };

  const saveEdit = () => {
    setValidaBotonEdit(false);
    axios
      .patch(
        `${URL_PROMOCIONES_WALMART}/${editId}`,
        {
          descuento: editDescuento,
          tipoDescuento: editTipoDescuento,
          fechaInicio: editFechaInicio,
          fechaFin: editFechaFin,
          marca: editMarca,
          categoria: editCategoria,
        },
        {
          headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
        }
      )
      .then(() => {
        Swal.fire({ icon: "success", title: "Promoción actualizada", timer: 1500, showConfirmButton: false });
        setModalEdit(false);
        fetchData();
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Error al actualizar";
        Swal.fire({ icon: "error", title: msg });
        setValidaBotonEdit(true);
      });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "¿Eliminar promoción?",
      text: "Se dará de baja esta promoción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .patch(
            `${URL_PROMOCIONES_WALMART}/${id}`,
            { is_active: "No" },
            {
              headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
            }
          )
          .then(() => {
            Swal.fire({ icon: "success", title: "Promoción eliminada", timer: 1200, showConfirmButton: false });
            fetchData();
          })
          .catch(() => {
            Swal.fire({ icon: "error", title: "Error al eliminar" });
          });
      }
    });
  };

  const filtered = useMemo(() => {
    let data = comments.filter((c) => c.is_active === "Si");

    if (filterFechaInicio) {
      data = data.filter((c) => c.fechaInicio >= filterFechaInicio);
    }
    if (filterFechaFin) {
      data = data.filter((c) => c.fechaFin <= filterFechaFin);
    }
    if (filterMarca) {
      data = data.filter((c) =>
        c.marca.toLowerCase().includes(filterMarca.toLowerCase())
      );
    }
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (c) =>
          c.nombre.toLowerCase().includes(q) ||
          c.marca.toLowerCase().includes(q) ||
          c.categoria.toLowerCase().includes(q) ||
          String(c.idPromocionWalmart).includes(q)
      );
    }

    // Sorting
    if (sorting.field) {
      const sorted = [...data].sort((a, b) => {
        const aVal = a[sorting.field] ?? "";
        const bVal = b[sorting.field] ?? "";
        if (aVal < bVal) return sorting.order === "asc" ? -1 : 1;
        if (aVal > bVal) return sorting.order === "asc" ? 1 : -1;
        return 0;
      });
      data = sorted;
    }

    setTotalItems(data.length);
    return data.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, search, sorting, currentPage, filterFechaInicio, filterFechaFin, filterMarca]);

  const rowStyle = (row) => {
    if (row.fechaFin < hoy) return { background: "#fee2e2" };
    if (row.fechaInicio <= hoy && hoy <= row.fechaFin) return { background: "#dcfce7" };
    return {};
  };

  if (!user.menu_ventas) {
    return (
      <div>
        <Header />
        <br />
        <center>
          <h4>Acceso denegado</h4>
        </center>
      </div>
    );
  }

  return (
    <>
      <Header />
      <br />
      <div className="container-fluid">
        <div className="card shadow-sm">
          {/* Toolbar */}
          <div
            className="card-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#fff",
              borderBottom: "1px solid #e2e8f0",
              padding: "12px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1e3a8a" }}>
                📣 Promociones Walmart
              </span>
              <span
                style={{
                  background: "#dbeafe",
                  color: "#1e3a8a",
                  borderRadius: 20,
                  padding: "3px 14px",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                }}
              >
                {totalItems} registros
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {user.ventas_create ? (
                <a href="/PromocionesWalmartCreate">
                  <Button size="sm" color="primary">
                    + Nueva
                  </Button>
                </a>
              ) : null}
              <a href="/MenuVentas">
                <Button size="sm" color="secondary" outline>
                  ← Regresar
                </Button>
              </a>
            </div>
          </div>

          <div className="card-body">
            {/* Filters */}
            <div style={block}>
              <p style={sec}>🔍 Filtros</p>
              <Row>
                <Col md={2}>
                  <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Fecha Inicio (desde)</Label>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={filterFechaInicio}
                    onChange={(e) => { setFilterFechaInicio(e.target.value); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={2}>
                  <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Fecha Fin (hasta)</Label>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={filterFechaFin}
                    onChange={(e) => { setFilterFechaFin(e.target.value); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={2}>
                  <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Marca</Label>
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="Filtrar marca..."
                    value={filterMarca}
                    onChange={(e) => { setFilterMarca(e.target.value); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={3}>
                  <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Búsqueda general</Label>
                  <Search
                    onSearch={(val) => { setSearch(val); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={3} style={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    onClick={() => {
                      setFilterFechaInicio("");
                      setFilterFechaFin("");
                      setFilterMarca("");
                      setSearch("");
                      setCurrentPage(1);
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: "0.78rem" }}>
              <span>
                <span style={{ display: "inline-block", width: 14, height: 14, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 3, marginRight: 4 }} />
                Vigente (activa hoy)
              </span>
              <span>
                <span style={{ display: "inline-block", width: 14, height: 14, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 3, marginRight: 4 }} />
                Vencida
              </span>
            </div>

            {/* Table */}
            <div className="table-responsive">
              <Table size="sm" bordered hover style={{ fontSize: "0.82rem" }}>
                <TableHeader
                  headers={TABLE_HEADERS}
                  onSorting={(field, order) => setSorting({ field, order })}
                />
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((row) => (
                      <tr key={row._id} style={rowStyle(row)}>
                        <td>{row.idPromocionWalmart}</td>
                        <td>{row.nombre}</td>
                        <td>{row.marca}</td>
                        <td>{row.categoria}</td>
                        <td style={{ textAlign: "right" }}>
                          {row.tipoDescuento === "Porcentaje"
                            ? `${row.descuento}%`
                            : `$${Number(row.descuento).toFixed(2)}`}
                        </td>
                        <td>{row.tipoDescuento}</td>
                        <td>{row.fechaInicio}</td>
                        <td>{row.fechaFin}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <ButtonGroup size="sm">
                            <Button
                              color="primary"
                              outline
                              onClick={() => openEdit(row)}
                            >
                              Editar
                            </Button>
                            <Button
                              color="danger"
                              outline
                              onClick={() => handleDelete(row._id)}
                            >
                              Eliminar
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center" style={{ color: "#94a3b8" }}>
                        Sin resultados
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </div>
      {loader}

      {/* Edit Modal — outside main card */}
      <Modal isOpen={modalEdit} toggle={() => setModalEdit(false)} size="lg">
        <ModalHeader toggle={() => setModalEdit(false)}>
          Editar Promoción
        </ModalHeader>
        <ModalBody>
          <div style={block}>
            <p style={sec}>📋 Detalles</p>
            <Row>
              <Col md={3}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Descuento</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  bsSize="sm"
                  value={editDescuento}
                  onChange={(e) => setEditDescuento(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Tipo Descuento</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={editTipoDescuento}
                  onChange={(e) => setEditTipoDescuento(e.target.value)}
                >
                  <option value="Porcentaje">Porcentaje</option>
                  <option value="Fijo">Fijo</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Fecha Inicio</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={editFechaInicio}
                  onChange={(e) => setEditFechaInicio(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Fecha Fin</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={editFechaFin}
                  onChange={(e) => setEditFechaFin(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Marca</Label>
                <Input
                  type="text"
                  bsSize="sm"
                  value={editMarca}
                  onChange={(e) => setEditMarca(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Label style={{ fontWeight: 600, fontSize: "0.8rem" }}>Categoría</Label>
                <Input
                  type="text"
                  bsSize="sm"
                  value={editCategoria}
                  onChange={(e) => setEditCategoria(e.target.value)}
                />
              </Col>
            </Row>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModalEdit(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={saveEdit} disabled={!validaBotonEdit}>
            {validaBotonEdit ? "Guardar" : <Spinner size="sm" />}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ListadoPromocionesWalmart;
